import { Input } from '../ui/Input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/Select';
import { Textarea } from '../ui/Textarea';
import type { FormFieldConfig } from './types';
import type { UseFormResult } from './useForm';

/** Renders the input for a field by `type`, fully wired to the form. */
export function renderFormControl<T, Ctx>(
  form: UseFormResult<T>,
  field: FormFieldConfig<T, Ctx>,
  ctx: Ctx,
  htmlFor: string,
) {
  const loose = form as unknown as UseFormResult<Record<string, unknown>>;
  const value = loose.values[field.id];
  const set = (next: unknown) => loose.set(field.id, next);
  const invalid = loose.errors[field.id] ? true : undefined;

  switch (field.type) {
    case 'select': {
      const options =
        typeof field.options === 'function' ? field.options(ctx) : (field.options ?? []);
      return (
        <Select value={value == null ? '' : String(value)} onValueChange={set}>
          <SelectTrigger id={htmlFor} aria-invalid={invalid}>
            <SelectValue placeholder={field.placeholder} />
          </SelectTrigger>
          <SelectContent>
            {options.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      );
    }
    case 'textarea':
      return (
        <Textarea
          id={htmlFor}
          aria-invalid={invalid}
          value={value == null ? '' : String(value)}
          placeholder={field.placeholder}
          autoFocus={field.autoFocus}
          rows={field.rows}
          onChange={(e) => set(e.target.value)}
        />
      );
    case 'number':
      return (
        <Input
          id={htmlFor}
          type="number"
          aria-invalid={invalid}
          value={value == null ? '' : String(value)}
          placeholder={field.placeholder}
          autoFocus={field.autoFocus}
          onChange={(e) => set(e.target.value === '' ? '' : Number(e.target.value))}
        />
      );
    default:
      return (
        <Input
          id={htmlFor}
          aria-invalid={invalid}
          value={value == null ? '' : String(value)}
          placeholder={field.placeholder}
          autoFocus={field.autoFocus}
          onChange={(e) => set(e.target.value)}
        />
      );
  }
}
